import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import MuiAutocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { localizedAutocomplete } from "./Autocomplete.localize.js";
import { useAutocompleteClasses } from "./Autocomplete.styles.js";
import { renderTags } from "./AutocompleteChip.js";
import { CloseIcon, PopupIcon } from "./AutocompleteIcons.js";
import { AutocompleteInfoMessage } from "./AutocompleteInfoMessage.js";
import { renderInput } from "./AutocompleteInput.js";
import { renderOption } from "./AutocompleteOption.js";
import { AutocompletePaper } from "./AutocompletePaper.js";
import { AutocompletePopper } from "./AutocompletePopper.js";
import { useAutocompleteState } from "./hooks.js";
const filter = createFilterOptions();
const Autocomplete = ({
  multiple,
  value,
  defaultOption,
  options,
  loading,
  triggerFilterChangeOnFirstOpen,
  suggestCreation,
  suggestPlaceholder,
  suggestAlways,
  clearOnSuggestionSelect,
  placeholder,
  classes,
  disabled,
  disablePortal,
  subInfoMessage,
  errorMessage,
  disableOptionSelection,
  allowEmptyValue,
  allowFreeInputValue,
  onValueChange,
  onFilterChange,
  optionsLimit,
  hasLargeOptions,
  errorMessageOrientation,
  dataTestId,
  mode = "client",
  useValueAsDefaultInputValue
}) => {
  const { classes: styles } = useAutocompleteClasses();
  const { formatMessage } = useIntl();
  const [isFilterChangeOnFirstOpenTriggered, setFilterChangeOnFirstOpenTriggered] = useState(false);
  const {
    open,
    showInputIcon,
    inputValue,
    selectedOption,
    optionsInternal,
    autocompleteState,
    handleFilterChange,
    handleInputChange,
    handleBlur,
    handleClear,
    handleClose,
    handleOpen,
    handleAutocompleteChange
  } = useAutocompleteState({
    useValueAsDefaultInputValue,
    value,
    defaultOption,
    options,
    onValueChange,
    onFilterChange,
    allowFreeInputValue,
    allowEmptyValue,
    multiple,
    mode
  });
  useEffect(() => {
    if (open && triggerFilterChangeOnFirstOpen && !isFilterChangeOnFirstOpenTriggered) {
      handleFilterChange(inputValue);
      setFilterChangeOnFirstOpenTriggered(true);
    }
  }, [open]);
  const getNoOptionsText = () => {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, formatMessage(inputValue === "" && placeholder ? placeholder : localizedAutocomplete.autocompleteNoOptionsLabel));
  };
  const getSuggestedOptionLabel = (inputValue2) => {
    return formatMessage(suggestPlaceholder ?? localizedAutocomplete.autocompleteAddNewItemLabel, {
      value: inputValue2
    });
  };
  const getOptionLabel = (option) => {
    if (typeof option === "string") {
      return option;
    }
    if (option.inputValue) {
      return option.inputValue;
    }
    if (option.isSuggested) {
      return getSuggestedOptionLabel(option.inputValue);
    }
    return option.label ?? "";
  };
  const getOptionValue = (option) => {
    return typeof option === "string" ? option : option.value;
  };
  const getOptionSelected = (option, value2) => {
    return getOptionLabel(option) === getOptionLabel(value2) && getOptionValue(option) === getOptionValue(value2);
  };
  const getOptions = (options2, params) => {
    const filterParams = { ...params, inputValue };
    let filteredOptions = mode === "client" ? filter(options2, filterParams) : options2;
    filteredOptions = optionsLimit ? filteredOptions.slice(0, optionsLimit) : filteredOptions;
    if (suggestCreation && (suggestAlways || !filteredOptions?.length && filterParams.inputValue !== "")) {
      const inputValue2 = clearOnSuggestionSelect ? "" : filterParams.inputValue;
      filteredOptions.push({
        inputValue: inputValue2,
        value: inputValue2,
        label: getSuggestedOptionLabel(inputValue2),
        isSuggested: true
      });
    }
    return filteredOptions;
  };
  const autocompleteProps = multiple ? {
    multiple: true,
    limitTags: 1,
    renderTags: (value2, getTagProps) => renderTags(value2, getTagProps, getOptionLabel),
    popupIcon: PopupIcon,
    closeIcon: CloseIcon
  } : {
    freeSolo: true
  };
  const largeOptionsClasses = hasLargeOptions ? {
    option: styles.autocompleteOptionLarge,
    popper: styles.autocompletePopperLarge
  } : {};
  const autocompleteRef = useRef(null);
  return /* @__PURE__ */ React.createElement("div", { className: styles.autocompleteContainer, ref: autocompleteRef }, /* @__PURE__ */ React.createElement(
    MuiAutocomplete,
    {
      ...autocompleteProps,
      "data-testid": dataTestId ?? "autocomplete",
      classes: { ...classes, ...largeOptionsClasses },
      disablePortal,
      loading,
      disabledItemsFocusable: true,
      autoComplete: true,
      disableClearable: true,
      selectOnFocus: true,
      openOnFocus: true,
      handleHomeEndKeys: true,
      disabled,
      value: selectedOption,
      open,
      onChange: handleAutocompleteChange,
      onInputChange: handleInputChange,
      onOpen: handleOpen,
      onClose: handleClose,
      onBlur: handleBlur,
      options: optionsInternal,
      noOptionsText: getNoOptionsText(),
      filterOptions: getOptions,
      getOptionLabel,
      isOptionEqualToValue: getOptionSelected,
      getOptionDisabled: () => disableOptionSelection,
      renderOption: (props, option, { inputValue: inputValue2 }) => renderOption(props, option, inputValue2),
      renderInput: (params) => renderInput(
        multiple,
        params,
        placeholder,
        loading,
        inputValue,
        handleClear,
        showInputIcon ? options?.find((o) => o.label === inputValue)?.startIcon : null
      ),
      slots: {
        paper: AutocompletePaper,
        popper: AutocompletePopper
      }
    }
  ), /* @__PURE__ */ React.createElement(
    AutocompleteInfoMessage,
    {
      subInfoMessage,
      autocompleteState,
      hideMessage: open,
      containerRef: autocompleteRef,
      dataTestId: "autocompleteErrorMessage",
      message: errorMessage,
      messageOrientation: errorMessageOrientation
    }
  ));
};
export {
  Autocomplete
};
//# sourceMappingURL=Autocomplete.js.map
